import { Component, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ModuleService } from 'src/app/services/modules/module.service';
import { RoleService } from 'src/app/services/role/role.service';
import Swal from 'sweetalert2';
declare var bootstrap: any;

@Component({
  selector: 'app-common-add-role-modal',
  templateUrl: './common-add-role-modal.component.html',
  styleUrls: ['./common-add-role-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CommonAddRoleModalComponent extends DestructorComponent {

    @Output() roleAdded = new EventEmitter<void>();

    modules: any[] = [];
    groupedModules: { [key: string]: any[] } = {};
    parentGroups: string[] = [];
    roleForm: FormGroup;
    isLoading: boolean = false;
    expandedParents: { [key: string]: boolean } = {};

    constructor(
      private _moduleService: ModuleService,
      private _roleService: RoleService,
      private fb: FormBuilder
    ){
      super();
      this.initializeForm();
    }

    ngOnInit(){
      this.GetModules();
    }

    initializeForm() {
      this.roleForm = this.fb.group({
        name: ['', [Validators.required]],
        select_all: [false],
        permissions: this.fb.group({})
      });
    }

    GetModules(){
        this.subscriptions.push(this._moduleService.GetData("1").subscribe({
          next: (res:any) => {
            this.modules = res.data?.data || [];
            this.groupModulesByParent();
            this.buildPermissionsForm();
          },
          error: (error) => {
            console.error('Error fetching modules:', error);
            // Use mock data for testing if API fails
            this.modules = this.getMockModules();
            this.groupModulesByParent();
            this.buildPermissionsForm();
          }
        }));
    }

    getMockModules() {
      return [
        {
          "_id": "68ab0318292a50e975756d29",
          "name": "Admin Users",
          "slug": "admin-users",
          "parent": "Common",
          "extra": ["bulk-operations", "export-data"],
          "children": [
            {
              "name": "Agents",
              "slug": "agents",
              "extra": ["invoice-generating", "report-access"]
            },
            { "name": "Roles", "slug": "roles" },
            { "name": "Permissions", "slug": "permissions" }
          ]
        },
        {
          "_id": "68ab0318292a50e975756d2b",
          "name": "Inquiries",
          "slug": "inquiries",
          "parent": "Common",
          "children": []
        },
        {
          "_id": "68ab0318292a50e975756d2c",
          "name": "Vehicle Management",
          "slug": "vehicle-management",
          "parent": "Operations",
          "extra": ["analytics-access"],
          "children": [
            {
              "name": "Cars",
              "slug": "cars",
              "extra": ["import-export"]
            },
            { "name": "Inventory", "slug": "inventory" }
          ]
        }
      ];
    }

    groupModulesByParent() {
      this.groupedModules = {};
      this.parentGroups = [];

      this.modules.forEach(module => {
        const parent = module.parent || 'Other';
        if (!this.groupedModules[parent]) {
          this.groupedModules[parent] = [];
          this.parentGroups.push(parent);
        }
        this.groupedModules[parent].push(module);
      });

      // Initialize all parents as expanded
      this.parentGroups.forEach(parent => {
        this.expandedParents[parent] = true;
      });
    }

    buildPermissionsForm() {
      const permissionsGroup = this.fb.group({});

      this.modules.forEach(module => {
        const moduleSlug = this.getModuleSlug(module);
        if (moduleSlug) {
          // Add module-level permissions
          const modulePermissions: any = {
            add: [false],
            edit: [false],
            view: [false]
          };

          // Add extra permissions for parent module if they exist
          if (module.extra && module.extra.length > 0) {
            module.extra.forEach((extraSlug: string) => {
              modulePermissions[extraSlug] = [false];
            });
          }

          permissionsGroup.addControl(moduleSlug, this.fb.group(modulePermissions));

          // Add child-level permissions
          if (module.children && module.children.length > 0) {
            module.children.forEach((child: any) => {
              const childSlug = child.slug;
              if (childSlug) {
                const childPermissions: any = {
                  add: [false],
                  edit: [false],
                  view: [false]
                };

                // Add extra permissions as additional checkboxes for this child
                if (child.extra && child.extra.length > 0) {
                  child.extra.forEach((extraSlug: string) => {
                    childPermissions[extraSlug] = [false];
                  });
                }

                permissionsGroup.addControl(childSlug, this.fb.group(childPermissions));
              }
            });
          }
        }
      });

      this.roleForm.setControl('permissions', permissionsGroup);
    }

    createSlugFromName(name: string): string {
      if (!name) return '';
      return name.toLowerCase()
                 .replace(/[^a-z0-9\s]/g, '')
                 .replace(/\s+/g, '_')
                 .trim();
    }

    onSelectAllChange(event: any) {
      const isChecked = event.target.checked;
      const permissionsControl = this.roleForm.get('permissions') as FormGroup;

      Object.keys(permissionsControl.controls).forEach(slug => {
        const group = permissionsControl.get(slug) as FormGroup;
        const patchValue: any = {
          add: isChecked,
          edit: isChecked,
          view: isChecked
        };

        // Check if this slug has extra permissions and add them to patch value
        // First check if it's a module
        const module = this.modules.find(m => this.getModuleSlug(m) === slug);
        if (module && module.extra && module.extra.length > 0) {
          module.extra.forEach((extraSlug: string) => {
            patchValue[extraSlug] = isChecked;
          });
        } else {
          // Check if it's a child
          for (const mod of this.modules) {
            if (mod.children) {
              const child = mod.children.find((c: any) => c.slug === slug);
              if (child && child.extra && child.extra.length > 0) {
                child.extra.forEach((extraSlug: string) => {
                  patchValue[extraSlug] = isChecked;
                });
                break;
              }
            }
          }
        }

        group.patchValue(patchValue);
      });
    }

    // Toggle parent accordion
    toggleParent(parent: string) {
      this.expandedParents[parent] = !this.expandedParents[parent];
    }

    // Handle child permission change and update parent accordingly
    onChildPermissionChange(module: any, childSlug: string, permission: string, event: any) {
      const isChecked = event.target.checked;
      const permissionsControl = this.roleForm.get('permissions') as FormGroup;

      // Update child permission
      const childGroup = permissionsControl.get(childSlug) as FormGroup;
      if (childGroup) {
        childGroup.get(permission)?.setValue(isChecked);
      }

      // Update parent module permission based on children
      this.updateParentPermission(module, permission);
    }

    // Handle module permission change and update children accordingly
    onModulePermissionChange(module: any, permission: string, event: any) {
      const isChecked = event.target.checked;
      const permissionsControl = this.roleForm.get('permissions') as FormGroup;

      // Update module permission
      const moduleGroup = permissionsControl.get(module.slug) as FormGroup;
      if (moduleGroup) {
        moduleGroup.get(permission)?.setValue(isChecked);
      }

      // Update all children permissions (including extra permissions)
      if (module.children && module.children.length > 0) {
        module.children.forEach((child: any) => {
          const childGroup = permissionsControl.get(child.slug) as FormGroup;
          if (childGroup) {
            // Only update standard permissions (add, edit, view) in children
            // Don't propagate extra permissions from parent to children
            if (['add', 'edit', 'view'].includes(permission)) {
              childGroup.get(permission)?.setValue(isChecked);
            }

            // Update extra permissions for this child only if it's a standard permission change
            if (['add', 'edit', 'view'].includes(permission) && child.extra && child.extra.length > 0) {
              child.extra.forEach((extraSlug: string) => {
                childGroup.get(extraSlug)?.setValue(isChecked);
              });
            }
          }
        });
      }
    }

    // Update parent permission based on children status
    updateParentPermission(module: any, permission: string) {
      if (!module.children || module.children.length === 0) return;

      const permissionsControl = this.roleForm.get('permissions') as FormGroup;
      const moduleGroup = permissionsControl.get(module.slug) as FormGroup;

      if (!moduleGroup) return;

      // Check if any child has this permission (including extra permissions)
      const hasAnyChildPermission = module.children.some((child: any) => {
        const childGroup = permissionsControl.get(child.slug) as FormGroup;
        if (!childGroup) return false;

        // Check standard permission
        if (childGroup.get(permission)?.value === true) return true;

        // Check extra permissions
        if (child.extra && child.extra.length > 0) {
          return child.extra.some((extraSlug: string) => {
            return childGroup.get(extraSlug)?.value === true;
          });
        }

        return false;
      });

      // Update parent permission
      moduleGroup.get(permission)?.setValue(hasAnyChildPermission);
    }

    onSubmit() {
      if (this.roleForm.valid) {
        this.isLoading = true;

        const formValue = this.roleForm.value;
        const formattedPermissions: any = {};

        // Format permissions according to the required structure: {slug}:{name:string,add:true,edit:true,view:false}
        Object.keys(formValue.permissions).forEach(slug => {
          // Find the module or child to get its name
          let itemName = slug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          let foundChild: any = null;

          // Check if it's a module
          const module = this.modules.find(m => this.getModuleSlug(m) === slug);
          if (module) {
            itemName = module.name;
          } else {
            // Check if it's a child
            for (const mod of this.modules) {
              if (mod.children) {
                const child = mod.children.find((c: any) => c.slug === slug);
                if (child) {
                  itemName = child.name;
                  foundChild = child;
                  break;
                }
              }
            }
          }

          // Create permission object with standard permissions
          const permissionObj: any = {
            name: itemName,
            add: formValue.permissions[slug].add || false,
            edit: formValue.permissions[slug].edit || false,
            view: formValue.permissions[slug].view || false
          };

          // Add extra permissions if they exist
          // First check if it's a module with extra permissions
          if (module && module.extra && module.extra.length > 0) {
            module.extra.forEach((extraSlug: string) => {
              permissionObj[extraSlug] = formValue.permissions[slug][extraSlug] || false;
            });
          } else if (foundChild && foundChild.extra && foundChild.extra.length > 0) {
            // Check if it's a child with extra permissions
            foundChild.extra.forEach((extraSlug: string) => {
              permissionObj[extraSlug] = formValue.permissions[slug][extraSlug] || false;
            });
          }

          formattedPermissions[slug] = permissionObj;
        });

        const submitData = {
          name: formValue.name,
          permissions: formattedPermissions
        };


        // Call the RoleService AddData method
        this.subscriptions.push(
          this._roleService.AddData(submitData).subscribe({
            next: () => {
              this.isLoading = false;

              // Show success message
              Swal.fire({
                text: "Role has been successfully created!",
                icon: "success",
                buttonsStyling: false,
                confirmButtonText: "Ok, got it!",
                customClass: {
                  confirmButton: "btn btn-primary"
                }
              }).then((result: any) => {
                if (result.isConfirmed) {
                  // Close the modal
                  this.closeModal();

                  // Emit event to refresh the listing
                  this.roleAdded.emit();

                  // Reset the form
                  this.resetForm();
                }
              });
            },
            error: (error: any) => {
              this.isLoading = false;
              console.error('Error creating role:', error);

              // Show error message
              Swal.fire({
                text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Ok, got it!",
                customClass: {
                  confirmButton: "btn btn-primary"
                }
              });
            }
          })
        );
      } else {
        // Mark all fields as touched to show validation errors
        this.roleForm.markAllAsTouched();

        // Show validation error in sweet alert
        Swal.fire({
          text: "Please fill in all required fields correctly.",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Ok, got it!",
          customClass: {
            confirmButton: "btn btn-primary"
          }
        });
      }
    }

    // Helper method to get permission control
    getPermissionControl(moduleSlug: string, permission: string): FormControl {
      return this.roleForm.get(['permissions', moduleSlug, permission]) as FormControl;
    }

    // Helper method to get module slug
    getModuleSlug(module: any): string {
      return module.slug || this.createSlugFromName(module.name);
    }

    // Helper method to close modal
    // Check if all permissions for a module are selected
    isModuleAllSelected(module: any): boolean {
      const permissionsControl = this.roleForm.get('permissions') as FormGroup;
      const moduleSlug = this.getModuleSlug(module);

      if (!moduleSlug) return false;

      // Check module permissions
      const moduleGroup = permissionsControl.get(moduleSlug) as FormGroup;
      if (!moduleGroup) return false;

      // Check if all standard permissions are selected
      const modulePermissions = ['add', 'edit', 'view'];
      const allModulePermsSelected = modulePermissions.every(perm =>
        moduleGroup.get(perm)?.value === true
      );

      // Check extra permissions for module
      if (module.extra && module.extra.length > 0) {
        const allExtraPermsSelected = module.extra.every((extraSlug: string) =>
          moduleGroup.get(extraSlug)?.value === true
        );
        if (!allExtraPermsSelected) return false;
      }

      if (!allModulePermsSelected) return false;

      // Check children permissions if any
      if (module.children && module.children.length > 0) {
        for (const child of module.children) {
          const childGroup = permissionsControl.get(child.slug) as FormGroup;
          if (!childGroup) return false;

          const allChildPermsSelected = modulePermissions.every(perm =>
            childGroup.get(perm)?.value === true
          );

          if (!allChildPermsSelected) return false;

          // Check extra permissions for child
          if (child.extra && child.extra.length > 0) {
            const allChildExtraPermsSelected = child.extra.every((extraSlug: string) =>
              childGroup.get(extraSlug)?.value === true
            );
            if (!allChildExtraPermsSelected) return false;
          }
        }
      }

      return true;
    }

    // Handle module select all change
    onModuleSelectAllChange(module: any, event: any) {
      const isChecked = event.target.checked;
      const permissionsControl = this.roleForm.get('permissions') as FormGroup;
      const moduleSlug = this.getModuleSlug(module);

      if (!moduleSlug) return;

      // Update module permissions
      const moduleGroup = permissionsControl.get(moduleSlug) as FormGroup;
      if (moduleGroup) {
        const patchValue: any = {
          add: isChecked,
          edit: isChecked,
          view: isChecked
        };

        // Add extra permissions for module
        if (module.extra && module.extra.length > 0) {
          module.extra.forEach((extraSlug: string) => {
            patchValue[extraSlug] = isChecked;
          });
        }

        moduleGroup.patchValue(patchValue);
      }

      // Update children permissions
      if (module.children && module.children.length > 0) {
        module.children.forEach((child: any) => {
          const childGroup = permissionsControl.get(child.slug) as FormGroup;
          if (childGroup) {
            const childPatchValue: any = {
              add: isChecked,
              edit: isChecked,
              view: isChecked
            };

            // Add extra permissions for child
            if (child.extra && child.extra.length > 0) {
              child.extra.forEach((extraSlug: string) => {
                childPatchValue[extraSlug] = isChecked;
              });
            }

            childGroup.patchValue(childPatchValue);
          }
        });
      }
    }

    closeModal() {
      const modalElement = document.getElementById('kt_modal_add_role');
      if (modalElement) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
    }

    // Helper method to reset form
    resetForm() {
      this.roleForm.reset();
      this.initializeForm();
      this.buildPermissionsForm();
    }
}
